import { PrintCardContentViewModel } from '../../shared/print-card-content-view-model';
import { Injectable } from '@angular/core';
import { map, shareReplay } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import { TheWoodsCardData } from '../../../../../../../models/print-cards/the-woods-card-data';
import { exists } from '../../../../../../../functions/exists';
import type { CannabinoidOrTerpeneFromPool } from '../../shared/card-data-with-property-pooling';
import { RegexUtils } from '../../../../../../../utils/regex-utils';
import { CompoundUtils } from '../../../../../../../utils/compound-utils';

@Injectable()
export class TheWoodsPostCardStackContentViewModel extends PrintCardContentViewModel {
  override readonly cardData$: Observable<TheWoodsCardData>; // only override the type for the type system

  public readonly customPropertiesBasedOnPools$ = combineLatest([
    this.cardData$,
    this.maxNumberOfPropertiesToUseForPropertyBasedPooling$,
    this.helperTotalTerpenes$,
    this.helperTAC$,
    this.helperTopTerp$,
    this.allPotentialPropertiesForSecondaryPool$
  ]).pipe(
    map(([cardData, maxProps, totalTerpenes, TAC, topTerp, secondaryProperties]) => {
      if (cardData?.isNonCannabinoidVariant()) return this.padProperties([], maxProps);
      const candidates = [totalTerpenes, TAC, topTerp, ...(secondaryProperties ?? [])].filter(prop => exists(prop));
      const properties = candidates.take(maxProps);
      const sortFinalListOfCompoundProperties = this.finalSortLambdaForCompoundProperties();
      if (sortFinalListOfCompoundProperties) properties.sort(sortFinalListOfCompoundProperties);
      return this.padProperties(this.applyFinalPropertyValueRules(properties), maxProps);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly colorStripWidth$ = this.cardData$.pipe(map(cardData => cardData?.colorStripWidth()));
  public readonly hasCraftDesignation$ = this.cardData$.pipe(map(cardData => cardData?.hasCraftDesignation()));
  public readonly strainTypeColor$ = this.cardData$.pipe(map(cardData => cardData?.getStrainTypeColor()));
  public readonly energyScore$ = this.cardData$.pipe(map(cardData => cardData?.getEnergyScore()));
  public readonly lineage$ = this.cardData$.pipe(map(cardData => cardData?.getLineage()));

  public readonly presentTerpenes$ = combineLatest([this.cardData$, this.helperPresentTerpenes$]).pipe(
    map(([cardData, presentTerpenes]) => (cardData?.isNonCannabinoidVariant() ? null : presentTerpenes?.value)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasPresentTerpenes$ = this.presentTerpenes$.pipe(
    map(presentTerpenes => exists(presentTerpenes)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly associatedEffects$ = this.cardData$.pipe(
    map(cardData => cardData?.getAssociatedEffects()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasAssociatedEffects$ = this.associatedEffects$.pipe(
    map(cardData => cardData?.length > 0),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly associatedDelimitedEffects$ = this.associatedEffects$.pipe(
    map(associatedEffects => associatedEffects?.join(', ')),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly shortDescription$ = this.cardData$.pipe(
    map(cardData => cardData?.getScopedVisibleVariants()?.firstOrNull()?.shortDescription || ''),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly hasShortDescription$ = this.shortDescription$.pipe(
    map(shortDescription => exists(shortDescription)),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly showLabelBelowPrice$ = this.atLeastOneGridVariantOnSale$.pipe(
    map(atLeastOneGridVariantOnSale => !atLeastOneGridVariantOnSale)
  );

  public readonly badgeMap$ = of({
    ...this.getImagesFor('Sativa'),
    ...this.getImagesFor('Indica'),
    ...this.getImagesFor('Hybrid'),
    ...this.getImagesFor('Blend'),
    ...this.getImagesFor('CBD')
  });

  public readonly badgeUrls$ = combineLatest([this.strainText$, this.energyScore$, this.badgeMap$]).pipe(
    map(([strainText, energyScore, badgeMap]) => {
      const strainBadges = badgeMap?.[strainText];
      if (!strainBadges) {
        return [];
      }
      if (!Number.isFinite(energyScore)) {
        return this.getEmptyEnergyScoreBadges(strainText, strainBadges);
      }
      return this.getEnergyScoreBasedBadges(energyScore, strainBadges);
    })
  );

  /**
   * The visible properties diverges from the pooling logic for this card. Present terpenes lives under its own
   * section called "Dominant Terpenes". Therefore, don't add it to the pooling logic.
   */
  protected override addVisiblePresentTerpsToPrimaryPool(
    _visiblePresentTerps: CannabinoidOrTerpeneFromPool | null,
    _maxProps: number,
    _properties: CannabinoidOrTerpeneFromPool[]
  ): void {
    return;
  }

  private applyFinalPropertyValueRules(properties: CannabinoidOrTerpeneFromPool[]): CannabinoidOrTerpeneFromPool[] {
    const emptyPropertyValue = this.emptyPropertyValueForPropertiesBasedOnPools();
    if (exists(emptyPropertyValue)) {
      return properties?.map(prop => this.withWoodsEmptyPropertyValueIfNeeded(prop, emptyPropertyValue));
    }
    return properties?.filter(prop => !this.woodsPropertyNumericValueIsTooSmall(prop));
  }

  private withWoodsEmptyPropertyValueIfNeeded(
    prop: CannabinoidOrTerpeneFromPool,
    emptyPropertyValue: string
  ): CannabinoidOrTerpeneFromPool {
    if (!this.propertyAsStringExists(prop?.value) || this.woodsPropertyNumericValueIsTooSmall(prop)) {
      return { ...prop, value: emptyPropertyValue };
    }
    return prop;
  }

  private woodsPropertyNumericValueIsTooSmall(prop: CannabinoidOrTerpeneFromPool): boolean {
    const numberString = prop?.value?.match(RegexUtils.parseNumbers)?.last();
    const parsedNumericValue = Number.parseFloat(numberString);
    const numericValue: number | null = Number.isNaN(parsedNumericValue) ? null : parsedNumericValue;
    return Number.isFinite(numericValue) && numericValue < CompoundUtils.smallestAllowableValue;
  }

  private padProperties(properties: CannabinoidOrTerpeneFromPool[], maxProps: number): CannabinoidOrTerpeneFromPool[] {
    const paddedProperties = [...(properties ?? [])];
    while (paddedProperties.length < maxProps) {
      paddedProperties.push({ title: ' ', value: ' ' });
    }
    return paddedProperties;
  }

  private getEmptyEnergyScoreBadges(strain: string, strainBadges: { [effect: string]: string }): string[] {
    if (strain === 'Sativa') {
      return [strainBadges['Active'], strainBadges['Captivate']];
    }
    if (strain === 'Indica') {
      return [strainBadges['Rest']];
    }
    if (strain === 'Hybrid') {
      return [strainBadges['Rest'], strainBadges['Unwind']];
    }
  }

  /**
   * |   Energy Score   |           Badges          |
   * |        1         |            rest           |
   * |        2         |        rest + unwind      |
   * |        3         |        rest + unwind      |
   * |        4         |     connect + unwind      |
   * |        5         |     connect + unwind      |
   * |        6         |     connect + captivate   |
   * |        7         |     connect + captivate   |
   * |        8         |      active + captivate   |
   * |        9         |      active + captivate   |
   * |       10         |           active          |
   */
  private getEnergyScoreBasedBadges(energyScore: number, strainBadges: { [effect: string]: string }): string[] {
    switch (energyScore) {
      case 1:
        return [strainBadges['Rest']];
      case 2:
        return [strainBadges['Rest'], strainBadges['Unwind']];
      case 3:
        return [strainBadges['Rest'], strainBadges['Unwind']];
      case 4:
        return [strainBadges['Connect'], strainBadges['Unwind']];
      case 5:
        return [strainBadges['Connect'], strainBadges['Unwind']];
      case 6:
        return [strainBadges['Connect'], strainBadges['Captivate']];
      case 7:
        return [strainBadges['Connect'], strainBadges['Captivate']];
      case 8:
        return [strainBadges['Active'], strainBadges['Captivate']];
      case 9:
        return [strainBadges['Active'], strainBadges['Captivate']];
      case 10:
        return [strainBadges['Active']];
    }
  }

  /**
   * The best I could do. The images are PNGs, and I couldn't get svgs from Figma in which I could modify the
   * internal colors, because the svgs just export with an internal <image> tag that points to a PNG.
   */
  private getImagesFor(strain: string): { [strain: string]: { [effect: string]: string } } {
    const parentPath = 'assets/images/the-woods-cannabis/';
    return {
      [strain]: {
        Active: `${parentPath}Strain=${strain}, Effect=Active.png`,
        Captivate: `${parentPath}Strain=${strain}, Effect=Captivate.png`,
        Connect: `${parentPath}Strain=${strain}, Effect=Connect.png`,
        Intimate: `${parentPath}Strain=${strain}, Effect=Intimate.png`,
        Rest: `${parentPath}Strain=${strain}, Effect=Rest.png`,
        Unwind: `${parentPath}Strain=${strain}, Effect=Unwind.png`
      }
    };
  }

  trackByUrl(index: number, url: string): string {
    return url;
  }
}
